import React, { useState, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Divider,
  InputAdornment,
  Box,
} from "@material-ui/core";
import {
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
} from "@material-ui/icons";
import {
  RocketLaunch,
  LibraryBooks,
  Image,
  MicNone,
  Videocam,
  DynamicFeed,
  AccessTime,
  CallSplit,
  Queue,
  Tag,
  Person,
  Campaign,
} from "@mui/icons-material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HttpIcon from "@mui/icons-material/Http";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { SiOpenai } from "react-icons/si";

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 48;

const nodeCategories = [
  {
    id: "basicos",
    name: "Basicos",
    color: "#6366f1",
    nodes: [
      {
        type: "start",
        label: "Inicio",
        icon: RocketLaunch,
        color: "#6366f1",
        description: "Ponto inicial do fluxo",
      },
    ],
  },
  {
    id: "conteudo",
    name: "Conteudo",
    color: "#22c55e",
    nodes: [
      {
        type: "singleBlock",
        label: "Bloco de Conteudo",
        icon: LibraryBooks,
        color: "#22c55e",
        description: "Enviar texto, imagem, audio ou video em um unico bloco",
      },
      {
        type: "img",
        label: "Imagem",
        icon: Image,
        color: "#22c55e",
        description: "Enviar imagem",
      },
      {
        type: "audio",
        label: "Audio",
        icon: MicNone,
        color: "#22c55e",
        description: "Enviar audio",
      },
      {
        type: "video",
        label: "Video",
        icon: Videocam,
        color: "#22c55e",
        description: "Enviar video",
      },
    ],
  },
  {
    id: "interacao",
    name: "Interacao",
    color: "#f59e0b",
    nodes: [
      {
        type: "menu",
        label: "Menu",
        icon: DynamicFeed,
        color: "#f59e0b",
        description: "Menu de opcoes para o usuario",
      },
      {
        type: "button",
        label: "Botoes",
        icon: TouchAppIcon,
        color: "#f59e0b",
        description: "Enviar mensagem com ate 3 botoes (WhatsApp)",
      },
      {
        type: "buttonPix",
        label: "Chave PIX",
        icon: AccountBalanceIcon,
        color: "#f59e0b",
        description: "Enviar chave PIX da empresa com botao Copiar",
      },
      {
        type: "list",
        label: "Lista",
        icon: ListAltIcon,
        color: "#f59e0b",
        description: "Enviar mensagem com lista (secoes e itens)",
      },
      {
        type: "input",
        label: "Input",
        icon: QuestionAnswerIcon,
        color: "#f59e0b",
        description: "Coletar entrada do usuario",
      },
      {
        type: "interval",
        label: "Intervalo",
        icon: AccessTime,
        color: "#f59e0b",
        description: "Pausar execucao por tempo",
      },
    ],
  },
  {
    id: "logica",
    name: "Logica",
    color: "#a855f7",
    nodes: [
      {
        type: "condition",
        label: "Condicional",
        icon: CallSplit,
        color: "#a855f7",
        description: "Bifurca o fluxo (sim/nao) baseado em comparacao",
      },
      {
        type: "randomizer",
        label: "Randomizador",
        icon: ShuffleIcon,
        color: "#a855f7",
        description: "Escolha aleatoria de caminhos",
      },
    ],
  },
  {
    id: "sistema",
    name: "Sistema",
    color: "#3b82f6",
    nodes: [
      {
        type: "ticket",
        label: "Filas",
        icon: Queue,
        color: "#3b82f6",
        description: "Gerenciar filas de atendimento",
      },
      {
        type: "tag",
        label: "Tags",
        icon: Tag,
        color: "#3b82f6",
        description: "Adicionar tags ao contato",
      },
      {
        type: "removeTag",
        label: "Remover Tag",
        icon: RemoveCircleOutlineIcon,
        color: "#3b82f6",
        description: "Remover tags do contato",
      },
      {
        type: "switchFlow",
        label: "Trocar Flow",
        icon: ArrowForwardIcon,
        color: "#3b82f6",
        description: "Direcionar para outro fluxo",
      },
      {
        type: "attendant",
        label: "Atendente",
        icon: Person,
        color: "#3b82f6",
        description: "Transferir para atendente",
      },
      {
        type: "campaignPro",
        label: "Campanha PRO",
        icon: Campaign,
        color: "#3b82f6",
        description: "Adicionar ou remover contato de campanha PRO",
      },
    ],
  },
  {
    id: "integracoes",
    name: "Integracoes",
    color: "#ef4444",
    nodes: [
      {
        type: "httpRequest",
        label: "HTTP Request",
        icon: HttpIcon,
        color: "#ef4444",
        description: "Requisicao HTTP externa",
      },
      {
        type: "variable",
        label: "Variavel",
        icon: DataObjectIcon,
        color: "#ef4444",
        description: "Definir variaveis globais",
      },
    ],
  },
  {
    id: "ia",
    name: "IA",
    color: "#8b5cf6",
    nodes: [
      {
        type: "openai",
        label: "OpenAI",
        iconCustom: "openai",
        color: "#8b5cf6",
        description: "Integracao com OpenAI (GPT)",
      },
      {
        type: "gemini",
        label: "Gemini",
        icon: PsychologyIcon,
        color: "#8b5cf6",
        description: "Integracao com Google Gemini",
      },
    ],
  },
  {
    id: "ixc",
    name: "IXC ISP",
    color: "#0ea5e9",
    nodes: [
      {
        type: "ixcBuscarCliente",
        label: "Buscar Cliente",
        icon: PersonSearchIcon,
        color: "#0ea5e9",
        description: "Buscar cliente no IXC por CPF/CNPJ",
      },
      {
        type: "ixcBuscarFatura",
        label: "Buscar Fatura",
        icon: ReceiptLongIcon,
        color: "#0ea5e9",
        description: "Buscar faturas em aberto no IXC",
      },
      {
        type: "ixcDesbloquear",
        label: "Desbloquear",
        icon: LockOpenIcon,
        color: "#0ea5e9",
        description: "Desbloqueio de confianca no IXC",
      },
    ],
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    transition: "width 250ms cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    zIndex: 10,
    flexShrink: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1),
    minHeight: 48,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    color: theme.palette.text.primary,
  },
  toggleBtn: {
    padding: 6,
  },
  searchField: {
    margin: theme.spacing(1, 1, 0.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: "0.8rem",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px",
    },
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(0.5, 1, 2),
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: 4,
    },
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 0.5),
    cursor: "pointer",
    borderRadius: 6,
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  categoryLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  categoryDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  categoryName: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  nodeItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(0.75, 1),
    marginBottom: 2,
    borderRadius: 6,
    cursor: "grab",
    userSelect: "none",
    transition: "background-color 150ms, transform 150ms",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateX(2px)",
    },
    "&:active": {
      cursor: "grabbing",
      opacity: 0.7,
    },
  },
  nodeIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": {
      fontSize: 16,
      color: "#fff",
    },
  },
  nodeLabel: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  collapsedIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: COLLAPSED_WIDTH,
    height: 36,
    cursor: "grab",
    borderRadius: 6,
    margin: "2px 0",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:active": {
      cursor: "grabbing",
      opacity: 0.7,
    },
    "& svg": {
      fontSize: 20,
    },
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(3, 1),
    color: theme.palette.text.disabled,
    fontSize: "0.8rem",
  },
}));

function NodeIcon({ node }) {
  if (node.iconCustom === "openai") {
    return <SiOpenai size={16} color="#fff" />;
  }
  const Icon = node.icon;
  return Icon ? <Icon style={{ fontSize: 16, color: "#fff" }} /> : null;
}

export default function NodePalette({ collapsed, onToggle, onAddNode }) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const initial = {};
    nodeCategories.forEach((cat) => {
      initial[cat.id] = true;
    });
    return initial;
  });

  const toggleCategory = useCallback((catId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catId]: !prev[catId],
    }));
  }, []);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return nodeCategories;
    const q = search.toLowerCase().trim();
    return nodeCategories
      .map((cat) => ({
        ...cat,
        nodes: cat.nodes.filter(
          (n) =>
            n.label.toLowerCase().includes(q) ||
            n.type.toLowerCase().includes(q) ||
            n.description.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.nodes.length > 0);
  }, [search]);

  const handleDragStart = useCallback((event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleNodeClick = useCallback(
    (nodeType) => {
      if (onAddNode) {
        onAddNode(nodeType, { x: 400, y: 300 });
      }
    },
    [onAddNode]
  );

  // --- Collapsed view (icon strip) ---
  if (collapsed) {
    return (
      <div className={classes.root} style={{ width: COLLAPSED_WIDTH }}>
        <div className={classes.header} style={{ justifyContent: "center", padding: "6px 4px" }}>
          <IconButton
            className={classes.toggleBtn}
            onClick={onToggle}
            size="small"
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.scrollArea} style={{ padding: "4px 6px" }}>
          {nodeCategories.map((cat, catIdx) => (
            <React.Fragment key={cat.id}>
              {catIdx > 0 && (
                <div style={{
                  height: 1, background: "#e0e0e0",
                  margin: "4px 4px",
                }} />
              )}
              {cat.nodes.map((node) => (
                <Tooltip
                  key={node.type}
                  title={node.label}
                  placement="right"
                  arrow
                >
                  <div
                    className={classes.collapsedIcon}
                    draggable
                    onDragStart={(e) => handleDragStart(e, node.type)}
                    onClick={() => handleNodeClick(node.type)}
                    style={{ width: 36, height: 36, margin: "2px auto" }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      backgroundColor: node.color,
                      display: "flex", alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <NodeIcon node={node} />
                    </div>
                  </div>
                </Tooltip>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  // --- Expanded view ---
  return (
    <div className={classes.root} style={{ width: EXPANDED_WIDTH }}>
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>Nodes</Typography>
        <IconButton
          className={classes.toggleBtn}
          onClick={onToggle}
          size="small"
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
      </div>

      <TextField
        className={classes.searchField}
        variant="outlined"
        size="small"
        placeholder="Buscar node..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ fontSize: 18, opacity: 0.5 }} />
            </InputAdornment>
          ),
        }}
      />

      <div className={classes.scrollArea}>
        {filteredCategories.length === 0 && (
          <div className={classes.noResults}>Nenhum node encontrado</div>
        )}

        {filteredCategories.map((category, idx) => (
          <React.Fragment key={category.id}>
            {idx > 0 && <Divider className={classes.divider} />}

            <div
              className={classes.categoryHeader}
              onClick={() => toggleCategory(category.id)}
            >
              <div className={classes.categoryLabel}>
                <span
                  className={classes.categoryDot}
                  style={{ backgroundColor: category.color }}
                />
                <Typography className={classes.categoryName}>
                  {category.name}
                </Typography>
              </div>
              {expandedCategories[category.id] ? (
                <ExpandLess style={{ fontSize: 18, opacity: 0.5 }} />
              ) : (
                <ExpandMore style={{ fontSize: 18, opacity: 0.5 }} />
              )}
            </div>

            <Collapse
              in={expandedCategories[category.id]}
              timeout="auto"
              unmountOnExit
            >
              {category.nodes.map((node) => (
                <Tooltip
                  key={node.type}
                  title={node.description}
                  placement="right"
                  arrow
                  enterDelay={400}
                >
                  <div
                    className={classes.nodeItem}
                    draggable
                    onDragStart={(e) => handleDragStart(e, node.type)}
                    onClick={() => handleNodeClick(node.type)}
                  >
                    <div
                      className={classes.nodeIcon}
                      style={{ backgroundColor: node.color }}
                    >
                      <NodeIcon node={node} />
                    </div>
                    <Typography className={classes.nodeLabel}>
                      {node.label}
                    </Typography>
                  </div>
                </Tooltip>
              ))}
            </Collapse>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
